import { InputAdornment, TextField } from "@mui/material";
import classNames from "classnames";
import { ChevronRight, Search } from "lucide-react";
import * as React from "react";

import styles from "./FieldPicker.module.css";
import { FieldTypeIcon } from "./fieldTypeIcons";
import type { FilterDefinition } from "./FilterDefinition";

interface FieldPickerProps {
  filterDefinitions: FilterDefinition[];
  /** Called when the user clicks a row or presses Enter on it. */
  onSelect: (definition: FilterDefinition) => void;

  /** The currently-selected definition, or null. Highlights the matching row. */
  selectedField: FilterDefinition | null;
}

/**
 * Left pane of the filter menu: searchable list of `FilterDefinition`s.
 */
export function FieldPicker({
  filterDefinitions,
  onSelect,
  selectedField,
}: FieldPickerProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // MUI Popover opens with a Grow transition; the Modal-derived focus
  // management that runs during it clobbers `autoFocus` on child inputs.
  // Request animation frame lets the transition settle for one frame, then
  // we claim focus deterministically. PropertyFilterMenu unmounts on close,
  // so this runs exactly once per open.
  React.useEffect(function focusSearchInput() {
    const handle = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(handle);
  }, []);

  const filtered = React.useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (needle === "") {
      return filterDefinitions;
    }
    return filterDefinitions.filter((def) =>
      def.name.toLowerCase().includes(needle),
    );
  }, [filterDefinitions, searchValue]);

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter" && filtered.length > 0) {
      event.preventDefault();
      onSelect(filtered[0]);
    }
  };

  return (
    <div className={styles.picker}>
      <div className={styles.search}>
        <TextField
          autoComplete="off"
          inputRef={searchInputRef}
          size="small"
          type="search"
          className={styles.searchInput}
          placeholder="Filter by property"
          aria-label="Search filterable fields"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={14} />
              </InputAdornment>
            ),
          }}
        />
      </div>
      {filtered.length === 0 ? (
        <div className={styles.empty}>No matching fields</div>
      ) : (
        <ul
          className={styles.list}
          role="listbox"
          aria-label="Filterable fields"
        >
          {filtered.map((definition) => {
            const isSelected = selectedField?.field === definition.field;
            return (
              <li key={definition.field} role="presentation">
                <button
                  role="option"
                  aria-selected={isSelected}
                  className={classNames(styles.row, {
                    [styles.rowSelected]: isSelected,
                  })}
                  onClick={() => onSelect(definition)}
                >
                  <span className={styles.rowIcon} aria-hidden="true">
                    {definition.icon ?? (
                      <FieldTypeIcon type={definition.type} />
                    )}
                  </span>
                  <span className={styles.rowName}>{definition.name}</span>
                  <ChevronRight
                    className={styles.rowChevron}
                    aria-hidden="true"
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
